import { Link } from "react-router-dom";
import { Truck, Calendar, MapPin, Phone } from "lucide-react";
import { usePageMeta } from "@/lib/seo";
import { STORE_ADDRESS, STORE_PHONE, STORE_HOURS, DELIVERY } from "@/constants/store";

export default function DeliveryPolicyPage() {
  usePageMeta("Delivery & Pickup Policy",
    "Delivery fees, scheduling, and pickup instructions for IPVALUE MARKETPLACE — serving Grand Rapids and West Michigan from our Grand Rapids, MI showroom. FREE delivery on orders $299+ within 10 miles. Tuesday · Thursday · Saturday delivery days.");
  return (
    <div className="max-w-4xl mx-auto px-6 md:px-8 py-12" data-testid="policy-delivery-page">
      <div className="ipv-eyebrow text-[#FFC220]">IPVALUE MARKETPLACE</div>
      <h1 className="font-display font-black text-4xl lg:text-5xl tracking-tight mt-2 text-[#0A1C40]">
        Delivery &amp; Pickup
      </h1>
      <p className="mt-4 text-lg text-gray-700 leading-relaxed max-w-2xl">
        Pick up free at our Grand Rapids, MI store, or let us deliver. Fees are quoted
        automatically at checkout based on driving distance from our showroom.
      </p>

      {/* Pickup */}
      <section className="mt-10 border-l-4 border-[#15803d] bg-[#f0fdf4] p-6" data-testid="policy-pickup-section">
        <div className="flex items-center gap-2 text-[#15803d]">
          <MapPin className="w-5 h-5" />
          <div className="ipv-eyebrow font-black">In-Store Pickup — Always Free</div>
        </div>
        <div className="mt-3 text-[15px] text-gray-800 leading-relaxed space-y-2">
          <p>
            <b>{STORE_ADDRESS.street}, {STORE_ADDRESS.city}, {STORE_ADDRESS.state} {STORE_ADDRESS.postal}</b>
          </p>
          <p>Store hours:</p>
          <ul className="ml-6 list-disc">
            {STORE_HOURS.displayLines.map((r) => (
              <li key={r.days}>{r.days}: {r.hours}</li>
            ))}
          </ul>
          <p>Available <b>same-day</b> during store hours. We'll text you when your order is ready.</p>
        </div>
      </section>

      {/* Delivery fee schedule */}
      <section className="mt-10" data-testid="policy-delivery-fees">
        <div className="flex items-center gap-2 text-[#0A1C40]">
          <Truck className="w-5 h-5" />
          <div className="ipv-eyebrow font-black">Standard Delivery Fees</div>
        </div>
        <div className="mt-4 border-2 border-[#0A1C40] overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#0A1C40] text-white text-left">
              <tr>
                <th className="p-3 font-black uppercase tracking-wider text-xs">Cart &amp; Distance</th>
                <th className="p-3 font-black uppercase tracking-wider text-xs text-right">Delivery Fee</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              <tr className="bg-[#f0fdf4]">
                <td className="p-3">Cart <b>$299+</b> &amp; within 10 miles</td>
                <td className="p-3 text-right font-black text-[#15803d]">$0.00 (FREE)</td>
              </tr>
              <tr>
                <td className="p-3">Cart under $299 &amp; within 10 miles</td>
                <td className="p-3 text-right font-black">$39.00</td>
              </tr>
              <tr>
                <td className="p-3">10.01 – 25 miles (any cart)</td>
                <td className="p-3 text-right font-black">$49.00</td>
              </tr>
              <tr>
                <td className="p-3">25.01 – 40 miles</td>
                <td className="p-3 text-right font-black">$49 + $2/mile over 25</td>
              </tr>
              <tr>
                <td className="p-3">Over 40 miles</td>
                <td className="p-3 text-right text-[#0A1C40] font-bold">Request a quote</td>
              </tr>
              <tr className="bg-[#FFC220]/10">
                <td className="p-3">Rush delivery upgrade (same or next day, subject to van availability)</td>
                <td className="p-3 text-right font-black">+$75.00</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-xs text-gray-500 leading-relaxed">
          Fees use <b>driving distance</b> from {STORE_ADDRESS.short}, not straight-line distance.
          If our mapping service can't calculate your fee, we'll ask you to call {STORE_PHONE}.
        </p>
      </section>

      {/* Over 40 mi */}
      <section className="mt-10 border-l-4 border-[#FFC220] bg-[#FFC220]/10 p-6" data-testid="policy-over-40mi">
        <div className="ipv-eyebrow font-black text-[#8a6a00]">Outside 40 miles?</div>
        <p className="mt-3 text-sm text-gray-800 leading-relaxed">
          You're outside our standard delivery area, but we can still help — we'll
          quote the trip or meet you partway. Call <a href={`tel:+1${STORE_PHONE.replace(/\D/g, "")}`} className="underline font-bold">{STORE_PHONE}</a> or add
          the item to your cart and pick "Request a delivery quote" at checkout.
        </p>
      </section>

      {/* Delivery days */}
      <section className="mt-10" data-testid="policy-delivery-scheduling">
        <div className="flex items-center gap-2 text-[#0A1C40]">
          <Calendar className="w-5 h-5" />
          <div className="ipv-eyebrow font-black">Delivery Days &amp; Cutoff</div>
        </div>
        <div className="mt-4 text-[15px] text-gray-800 space-y-2 leading-relaxed">
          <p>We deliver <b>Tuesday · Thursday · Saturday</b> only.</p>
          <p>
            Orders paid in full by <b>2:00 PM</b> are eligible for the next delivery day.
            Orders paid after 2 PM roll to the following delivery day.
          </p>
          <p>
            <b>Saturday deliveries run 9:00 AM – 1:00 PM.</b> Weekday deliveries run
            throughout the day; you'll get a 3-hour window text the day before.
          </p>
          <p>Most orders arrive in <b>2 to 4 days</b> after payment.</p>
        </div>
      </section>

      {/* Payment */}
      <section className="mt-10 border-l-4 border-gray-300 bg-gray-50 p-6" data-testid="policy-payment">
        <div className="ipv-eyebrow font-black text-[#0A1C40]">Payment</div>
        <div className="mt-3 text-sm text-gray-800 space-y-2 leading-relaxed">
          <p><b>Payment in full is required</b> before your order is scheduled for delivery. No cash on delivery — our drivers do not collect money.</p>
          <p>Cash payment is available <b>for in-store purchases only</b>.</p>
          <p>Card processing fee (if applicable) is shown as its own line before payment — never buried in the total.</p>
        </div>
      </section>

      {/* Pallet delivery */}
      <section className="mt-10 border-l-4 border-[#FFC220] bg-[#FFC220]/10 p-6" data-testid="policy-pallet-delivery">
        <div className="ipv-eyebrow font-black text-[#8a6a00]">Pallet Delivery — Different Rates</div>
        <div className="mt-3 text-sm text-gray-800 space-y-2 leading-relaxed">
          <p>Pallets use flat delivery rates that <b>do not use the standard tiers above</b>:</p>
          <ul className="ml-6 list-disc">
            <li>$75 within 10 miles</li>
            <li>$99 within 25 miles</li>
            <li>Beyond 25 miles: request a quote</li>
          </ul>
          <p>Pallet delivery is <b>curbside/driveway only</b> — we do not carry pallets inside. Pallets never qualify for the $299 free-delivery threshold.</p>
          <Link to="/policies/pallets" className="inline-block mt-2 text-[#0A1C40] font-bold underline text-xs">See full pallet policy →</Link>
        </div>
      </section>

      <div className="mt-12 text-center">
        <Link to="/shop" className="btn-gold inline-flex items-center gap-2" data-testid="policy-back-to-shop">
          Back to shopping
        </Link>
      </div>
      <div className="mt-6 text-center text-xs text-gray-500">
        <Phone className="w-3 h-3 inline mr-1" /> Questions? Call {STORE_PHONE}.
      </div>
    </div>
  );
}
